// AmsCallCentre.tsx — the call centre's day.
//
// THE EXCLUSION NOTICE IS THE MOST IMPORTANT THING ON THIS SCREEN. The server removes households
// protected by hardship rules 1B, 1D, 1E and 1F — deferrals and hard stops, where the Town Council
// has decided not to pursue — and this screen says so, with the count. Filtering them out silently
// would be safe but opaque; leaving them in with a warning tag would eventually put a collections
// call through to someone in exactly the circumstances the rule exists to protect. Stating the
// exclusion is the only version that is both safe and honest.
//
// THE SCRIPT IS ON SCREEN, not in a binder, because the fourth line of it is the one that matters:
// "has anything changed that makes paying hard right now?" is what turns a collections call into
// the diagnostic step the whole operating model depends on.
//
// An agent records what was said. They do not classify, suppress, approve or send — see
// capabilities.ts::'arrears.contact.log'. "Says they cannot pay" produces a referral for
// assessment, not a Category 1.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError, type ArrearsCase } from '../../lib/api'
import { CATEGORIES, money } from '../../lib/format'
import RecordContact from './RecordContact'

type Queue = Awaited<ReturnType<typeof api.ams.callQueue>>

const SCRIPT = [
  'Confirm you are speaking to the account holder.',
  'State the amount owed and how long it has been overdue.',
  'Ask: has anything changed that makes paying hard right now?',
  'If they can pay: agree a date. If not: listen, write down the reason, and say the Community ' +
  'Liaison Team will be in touch.',
  'Tell them about instalment plans and how to pay.',
]

export default function AmsCallCentre() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [d, setD] = useState<Queue | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sel, setSel] = useState<ArrearsCase | null>(null)

  const load = () => {
    if (!org) return
    api.ams.callQueue(org, 40)
      .then(q => { setD(q); setSel(s => s ? q.cases.find(c => c.id === s.id) ?? q.cases[0] ?? null : q.cases[0] ?? null) })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }
  useEffect(load, [org])

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!d) return <div className="page-state">Loading…</div>

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Call centre</h2>
          <p className="muted">
            Today&apos;s calls, ranked by how likely the account is to recover if contacted now.
          </p>
        </div>
        <div className="chips">
          <span className="tag blue">{d.cases.length} to call</span>
        </div>
      </header>

      {/* Never remove this. See the file header. */}
      <div className="notice ok">
        <b>{d.excluded.protectedByRule.toLocaleString()} households are not on this list.</b>{' '}
        They are protected by hardship rules {d.excluded.rules.join(', ')} — deferrals and hard
        stops, where the Town Council has decided not to pursue. Calling them to ask for money
        would undo the protection.
        {d.excluded.underAssessment > 0 && (
          <p>
            A further {d.excluded.underAssessment} are with the Community Liaison Team for
            assessment. They are being visited; a call now would contradict that.
          </p>
        )}
      </div>

      <div className="cc">
        <div className="cc-list">
          {d.cases.map(c => (
            <button key={c.id} className={`cc-item${sel?.id === c.id ? ' on' : ''}`}
                    onClick={() => setSel(c)}>
              <div className="cc-item-top">
                <b>{c.residentName}</b>
                <span className="score"><b>{c.priorityScore}</b></span>
              </div>
              <div className="muted xs">
                {money(c.balanceCents)} · {c.daysOverdue} days · {CATEGORIES[c.category ?? 'unsegmented']}
              </div>
            </button>
          ))}
          {d.cases.length === 0 && (
            <p className="muted sm">
              Nobody to call. That is a real result, not a failed load.
            </p>
          )}
        </div>

        {sel && (
          <div className="cc-work">
            <section className="card">
              <div className="cc-who">
                <div>
                  <h3>{sel.residentName}</h3>
                  <div className="muted sm">{sel.address} · {sel.accountNo}</div>
                </div>
                <div className="cc-owed">
                  <div className="kpi-label">Owed</div>
                  <b>{money(sel.balanceCents)}</b>
                  <div className="muted xs">{sel.daysOverdue} days overdue</div>
                </div>
              </div>
              <div className="actions cc-actions">
                {/* No dialler is wired up, and the button does not pretend there is one. */}
                <Link className="btn" to={`/ams/inquiry?case=${sel.id}`}>See every touchpoint</Link>
                <Link className="btn" to={`/ams/cases/${sel.id}`}>Open the case</Link>
              </div>
              <p className="muted xs cc-nodial">
                Dialling is not connected — place the call on your handset, then record it below.
              </p>
            </section>

            <section className="card">
              <h3>Call script</h3>
              <ol className="cc-script">
                {SCRIPT.map((line, i) => <li key={i}>{line}</li>)}
              </ol>
            </section>

            {/* The same component the case page uses. One contact form, not two that drift. */}
            <RecordContact org={org!} caseId={sel.id} resident={sel.residentName}
                           onSaved={load} startOpen />
          </div>
        )}
      </div>
    </div>
  )
}
